import React from 'react'
import { ModuleID } from '../types'
import PreviewModule from '../preview/Module'
import ThemeEditorModule from '../theme_editor/Module'
import SurveyToolbar from '../../session/SurveyToolbar/SurveyToolbar'
import DevLog from '../devlog/Module'
import { IToolbarModule, IConfigProps, IToolbarManagerProps } from './types'
import { layoutIsCompact } from '../../lib/layout'

const currentToolbarProps: IToolbarManagerProps = {
  toolbar: {}
}
let currentToolbarType: ModuleID.PREVIEW | ModuleID.THEME_EDITOR = null

const DEFAULT_TAGLINE = 'Your participation is highly appreciated!'
const toolbars = {
  theme_editor: {
    load: async () => {
      DevLog._log(`SW[${ModuleID.TOOLBAR}]:`, 'load ThemeEditorModule')
      return ThemeEditorModule.config(currentToolbarProps).load()
    }
  },
  preview: {
    load: async () => {
      DevLog._log(`SW[${ModuleID.TOOLBAR}]:`, 'load PreviewModule')
      return PreviewModule.config(currentToolbarProps).load()
    }
  }
}

let moduleLoaded = false
const ToolbarModule: IToolbarModule = {
  get id() {
    return ModuleID.TOOLBAR
  },
  get loaded() {
    return moduleLoaded
  },
  config({ type, props }: IConfigProps = {}) {
    const {
      toolbar,
      closeable,
      headless,
      resetURL,
      accentColors,
      currentTheme,
      defaultTheme,
      saveThemeURL,
      predefinedThemes,
      imageAssetCreateUrl,
      imageAssetDeleteUrl,
      authenticityToken
    } = props
    currentToolbarType = type
    if (null != toolbar?.show) {
      currentToolbarProps.toolbar.show = toolbar.show
    }
    if (null != toolbar?.hide) {
      currentToolbarProps.toolbar.hide = toolbar.hide
    }
    if (null != toolbar?.collapsible) {
      currentToolbarProps.toolbar.collapsible = toolbar.collapsible
    }
    if (null != toolbar?.thin) {
      currentToolbarProps.toolbar.thin = toolbar.thin
    }
    if (null != toolbar?.controls) {
      currentToolbarProps.toolbar.controls = toolbar.controls
    }
    if (null != closeable) {
      currentToolbarProps.closeable = closeable
    }
    if (null != headless) {
      currentToolbarProps.headless = headless
    }
    if (null != resetURL) {
      currentToolbarProps.resetURL = resetURL
    }
    if (accentColors?.length) {
      currentToolbarProps.accentColors = accentColors
    }
    if (null != currentTheme) {
      currentToolbarProps.currentTheme = currentTheme
    }
    if (null != defaultTheme) {
      currentToolbarProps.defaultTheme = defaultTheme
    }
    if (null != saveThemeURL) {
      currentToolbarProps.saveThemeURL = saveThemeURL
    }
    if (null != predefinedThemes) {
      currentToolbarProps.predefinedThemes = predefinedThemes
    }
    if (null != imageAssetCreateUrl) {
      currentToolbarProps.imageAssetCreateUrl = imageAssetCreateUrl
    }
    if (null != imageAssetDeleteUrl) {
      currentToolbarProps.imageAssetDeleteUrl = imageAssetDeleteUrl
    }
    if (null != authenticityToken) {
      currentToolbarProps.authenticityToken = authenticityToken
    }
    return ToolbarModule
  },

  async load() {
    try {
      if (null != currentToolbarType) {
        await toolbars[currentToolbarType]?.load?.()
      }
      moduleLoaded = true
    } catch (error) {
      DevLog.moduleError(ToolbarModule, error, 'load ToolbarModule')
    }
    return ToolbarModule
  },

  renderThemeEditorToolbar({ setCollapsedToolbar, logoURL }) {
    return ThemeEditorModule.loaded ? (
      <ThemeEditorModule.ThemeEditorToolbar
        props={{ ...currentToolbarProps, logoURL }}
        onCollapse={state => setCollapsedToolbar(state)}
      />
    ) : null
  },

  renderPreviewToolbar({ setCollapsedToolbar }) {
    return PreviewModule.loaded ? (
      <PreviewModule.PreviewToolbar onCollapse={state => setCollapsedToolbar(state)} />
    ) : null
  },

  renderClosePreviewButton({ onClosePreview, className, style }) {
    return PreviewModule.loaded && PreviewModule.closeable ? (
      <PreviewModule.ClosePreviewButton onClick={onClosePreview} className={className} style={style} />
    ) : null
  },

  renderSurveyToolbar({ sessionProps, sessionAPI, action, collapsedToolbar }) {
    if (currentToolbarProps.toolbar.hide) return null
    return !layoutIsCompact(sessionProps.layout) &&
      ((sessionAPI.style.tagline &&
        (!(PreviewModule.loaded && PreviewModule.show) || DEFAULT_TAGLINE != sessionAPI.style.tagline)) ||
        sessionAPI.style.logo) ? (
      <SurveyToolbar
        logo={sessionAPI.style.logo}
        tagline={sessionAPI.style.tagline}
        preview={action?.preview && !(PreviewModule.loaded && PreviewModule.show)}
        secondary={PreviewModule.loaded && PreviewModule.show && !collapsedToolbar}
      />
    ) : null
  },

  get resetURL() {
    return currentToolbarProps.resetURL
  }
}

export default ToolbarModule
